import { type FunctionComponent, useEffect } from 'react'
import { type SelectChangeEvent } from '@mui/material'
import { useLanguage } from '@/context/LanguageContext'
import useIntl from '@/utils/intl'
import { resolveImageUrl } from '@/utils/constants'
import LocationSelect from '../LocationSelect'
import { type City, type District, type MapState, type Overlay, type Quarter, type SelectItem } from '@/lib/network-coverage-v2/types'
import { NETWORK_TYPES } from '@/lib/network-coverage-v2/networkCoverageConstants'
import { localeName } from '@/lib/network-coverage-v2/helpers'

interface NetworkCoverageTabProps {
  mapState: MapState
  onMapStateChange: (partial: Partial<MapState>) => void
  selectedCity: City | null
  selectedDistrict: District | null
  quarters: Quarter[]
  cityItems: SelectItem[]
  districtItems: SelectItem[]
  iconColor: string
  onCitySelect: (zipcode: string, options?: { updateMapCenter?: boolean, onCityFound?: (city: City) => void }) => void
  onDistrictSelect: (zipcode: string, options?: { updateMapCenter?: boolean }) => void
  onNetworkTypeSelect: (event: SelectChangeEvent<string>) => void
  onUserLocationChange: (location: { lat: number, lng: number } | null) => void
}

const NetworkCoverageTab: FunctionComponent<NetworkCoverageTabProps> = ({
  mapState,
  onMapStateChange,
  selectedCity,
  selectedDistrict,
  quarters,
  cityItems,
  districtItems,
  iconColor,
  onCitySelect,
  onDistrictSelect,
  onNetworkTypeSelect,
  onUserLocationChange
}) => {
  const { translate } = useIntl()
  const { locale } = useLanguage()

  useEffect(() => {
    if (selectedCity === null || mapState === undefined || mapState === null) return
    const cityOverlays = selectedCity.overlays
    const overlayMap: Record<string, Overlay[] | null | undefined> = {
      '4G': cityOverlays?.fourG,
      '5G': cityOverlays?.fiveG,
      '3G': cityOverlays?.threeG
    }
    const activeOverlays = mapState.ncType !== undefined ? overlayMap[mapState.ncType] : undefined
    if ((activeOverlays?.length ?? 0) > 0) {
      onMapStateChange({ kmlContent: '', overlays: activeOverlays })
    } else {
      const kmlMap: Record<string, string | undefined> = {
        '4G': selectedCity.fourGkmz,
        '5G': selectedCity.fiveGkmz
      }
      const kmz = mapState.ncType !== undefined ? kmlMap[mapState.ncType] : undefined
      const kmlContent = (kmz != null && kmz !== '') ? resolveImageUrl(kmz) : ''
      onMapStateChange({ kmlContent, overlays: undefined })
    }
  }, [selectedCity?.zipcode, mapState.ncType, selectedCity?.overlays])

  const applyCity = (zipcode: string, userLat?: number, userLng?: number): void => {
    if (userLat !== undefined && userLng !== undefined) {
      onUserLocationChange({ lat: userLat, lng: userLng })
      onMapStateChange({ center: { lat: userLat, lng: userLng }, zoom: 12 })
      onCitySelect(zipcode, { updateMapCenter: false })
    } else {
      onUserLocationChange(null)
      onCitySelect(zipcode, { updateMapCenter: true })
    }
  }

  const handleNcCitySelect = (event: SelectChangeEvent<string>): void => {
    applyCity(event.target.value)
  }

  const handleDistrictSelect = (event: SelectChangeEvent<string>): void => {
    onDistrictSelect(event.target.value, { updateMapCenter: false })
  }

  // Show district for both 4G and 5G; hide only for UB city (type === 'City')
  const showDistrictSelect = (
    selectedCity !== null &&
    districtItems.length > 0 &&
    selectedCity.type !== 'City'
  )

  return (
    <div>
      <div className='p-6 border-b border-secondaryMidStroke'>
        <LocationSelect
          label={translate('networktype')}
          value={mapState?.ncType ?? ''}
          items={NETWORK_TYPES.map((t) => ({
            key: String(t.id),
            value: locale === 'mn' ? t.nameMN : t.nameEN,
            label: locale === 'mn' ? t.nameMN : t.nameEN
          }))}
          onChange={onNetworkTypeSelect}
          iconColor={iconColor}
        />
        {mapState.ncType !== undefined && (
          <LocationSelect
            label={translate('city')}
            value={selectedCity?.zipcode ?? ''}
            items={cityItems}
            onChange={handleNcCitySelect}
            iconColor={iconColor}
          />
        )}
        {showDistrictSelect && (
          <LocationSelect
            label={translate('district')}
            value={selectedDistrict?.zipcode ?? ''}
            items={districtItems}
            onChange={handleDistrictSelect}
            iconColor={iconColor}
          />
        )}
      </div>
      {selectedDistrict !== null && selectedCity?.type !== 'City' && (
        <div className='p-6 ml-10 border-secondaryMidStroke'>
          {quarters.map((quarter) => (
            <div key={quarter.id} className='flex justify-between items-end'>
              <div className='w-3/6'>
                <p className='text-textHigh text-base font-medium'>
                  {localeName(quarter, locale)}
                </p>
              </div>
              <div className='w-3/6'>
                <span className='text-red-500 text-lg font-bold whitespace-pre-line'>
                  {quarter.twoG === true ? '2G ' : ''}
                  {quarter.threeG === true ? '3G ' : ''}
                  {quarter.fourG === true ? '4G ' : ''}
                  {quarter.fiveG === true ? '5G' : ''}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default NetworkCoverageTab
